import { useEffect, useId, useRef, useState, type ReactNode } from "react";

/** An "ⓘ" button that toggles a panel; Escape, a click outside or focus leaving closes it. Works without hover. */
export default function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointer = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  return (
    <div
      ref={root}
      className="relative inline-block"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className="rounded-full px-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-accent"
      >
        ⓘ
      </button>
      <div
        id={panelId}
        hidden={!open}
        tabIndex={-1}
        className="absolute left-0 z-10 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-md border border-gray-200 bg-white p-3 text-xs text-gray-600 shadow-card focus:outline-none"
      >
        {children}
      </div>
    </div>
  );
}
